import { useState } from "react";
import {
  SearchIcon,
  CheckIcon,
  LoaderIcon,
  XCircleIcon,
  ChevronDownIcon,
  ExternalLinkIcon,
} from "lucide-react";
import { type ToolCallMessagePartComponent } from "@assistant-ui/react";
import { cn } from "../lib/utils";

// Compact one-line renderer for the `search_knowledge_base` /
// `search_agrana_emails` tools — much denser than the generic
// `ToolFallback` accordion, which is overkill for a search that
// returns "queried RAG, N hits". Other tool calls keep ToolFallback.
//
// Expandable: click anywhere on the pill (chevron flips) to drill
// into the parsed result_preview — article titles, sources, scores,
// publish dates, and clickable URLs. Closed by default so the chain-
// of-thought stays compact (Claude / ChatGPT pattern).
function parseHitCount(result: unknown): number | null {
  if (typeof result !== "string") return null;
  const m =
    result.match(/(\d+)\s+(?:result|hit|document|email|article)s?\b/i) ||
    result.match(/^\s*found\s+(\d+)/i);
  return m ? parseInt(m[1], 10) : null;
}

// Parses the backend's `result_preview` for the search tools into a
// structured article list. Backend emits one block per hit shaped:
//
//   Article 1 (Score: 0.62)
//     Title: <title>
//     Source: <domain> | Published: <YYYY-MM-DD>
//     URL: https://...
//
// (Sometimes "Source" is missing or fields are reordered — we capture
// each line independently and tolerate missing ones.) Falls back to
// the raw preview text when nothing matched, so the user never loses
// access to what backend sent.
type ParsedHit = {
  index: number;
  score: number | null;
  title: string | null;
  source: string | null;
  published: string | null;
  url: string | null;
};

function parseHits(result: unknown): ParsedHit[] {
  if (typeof result !== "string" || !result.trim()) return [];
  const blocks = result.split(/\n(?=Article\s+\d+|Email\s+\d+|Document\s+\d+)/i);
  const hits: ParsedHit[] = [];
  for (const block of blocks) {
    const indexMatch = block.match(
      /^(?:Article|Email|Document)\s+(\d+)(?:\s*\(Score:\s*([\d.]+)\))?/i,
    );
    if (!indexMatch) continue;
    const title = block.match(/^\s*Title:\s*(.+)$/im)?.[1]?.trim() ?? null;
    const source = block.match(/^\s*Source:\s*([^|]+?)(?:\s*\||$)/im)?.[1]?.trim() ?? null;
    const published = block.match(/Published:\s*([^\n]+)/i)?.[1]?.trim() ?? null;
    const url = block.match(/^\s*URL:\s*(\S+)/im)?.[1]?.trim() ?? null;
    hits.push({
      index: parseInt(indexMatch[1], 10),
      score: indexMatch[2] ? parseFloat(indexMatch[2]) : null,
      title,
      source,
      published,
      url,
    });
  }
  return hits;
}

const SearchKnowledgeBaseInline: ToolCallMessagePartComponent = ({
  argsText,
  result,
  status,
}) => {
  const statusType = status?.type ?? "complete";
  const isRunning = statusType === "running";
  const isError = statusType === "incomplete";
  const hits = parseHitCount(result);
  const parsedHits = parseHits(result);
  const hasDetails = parsedHits.length > 0 || (typeof result === "string" && result.trim().length > 0);
  const [open, setOpen] = useState(false);

  const StatusIcon = isRunning ? LoaderIcon : isError ? XCircleIcon : CheckIcon;
  const query = (argsText ?? "").trim();

  return (
    <div
      data-slot="search-kb-inline-root"
      className={cn(
        "aui-search-kb-inline-root w-full rounded-md border border-border bg-card text-sm",
        isRunning && "border-l-2 border-l-primary bg-primary/5",
        isError && "border-destructive/40 bg-destructive/5",
      )}
    >
      <button
        type="button"
        data-slot="search-kb-inline"
        data-state={open ? "open" : "closed"}
        onClick={() => hasDetails && setOpen((v) => !v)}
        disabled={!hasDetails || isRunning}
        className={cn(
          "aui-search-kb-inline group/search-kb flex w-full items-center gap-2 px-3 py-1.5 text-start",
          hasDetails && !isRunning ? "cursor-pointer" : "cursor-default",
          hasDetails && !isRunning && "hover:bg-muted/40",
        )}
        aria-expanded={hasDetails ? open : undefined}
      >
        <SearchIcon className="size-4 shrink-0 text-muted-foreground" />
        {/* When backend forwards the query (step.start.detail) we
            render 'Searched: "X"' — current upstream limitation
            ([[project-thinking-timeline-extension]]) means detail is
            usually null, so fall back to "Searched knowledge base"
            instead of an empty-looking placeholder. */}
        {query ? (
          <>
            <span className="aui-search-kb-inline-label text-muted-foreground">
              Searched:
            </span>
            <span
              className="aui-search-kb-inline-query flex-1 truncate font-medium text-foreground"
              title={query}
            >
              "{query}"
            </span>
          </>
        ) : (
          <span className="aui-search-kb-inline-label flex-1 truncate text-foreground">
            Searched knowledge base
          </span>
        )}
        <StatusIcon
          className={cn(
            "size-3.5 shrink-0",
            isRunning && "animate-spin text-primary",
            !isRunning && !isError && "text-emerald-600",
            isError && "text-destructive",
          )}
        />
        {hits !== null && !isRunning && (
          <span className="aui-search-kb-inline-count text-muted-foreground text-xs tabular-nums">
            {hits} {hits === 1 ? "hit" : "hits"}
          </span>
        )}
        {hasDetails && !isRunning && (
          <ChevronDownIcon
            className={cn(
              "ms-1 size-3.5 shrink-0 text-muted-foreground transition-transform duration-200",
              open ? "rotate-0" : "-rotate-90",
            )}
          />
        )}
      </button>
      {open && hasDetails && (
        <div
          data-slot="search-kb-inline-details"
          className="aui-search-kb-inline-details space-y-2 border-t border-border/60 px-3 py-2 text-xs"
        >
          {parsedHits.length > 0 ? (
            <ul className="aui-search-kb-inline-hits flex flex-col gap-2">
              {parsedHits.map((hit) => (
                <li
                  key={`${hit.index}-${hit.url ?? hit.title}`}
                  className="aui-search-kb-inline-hit flex flex-col gap-0.5"
                >
                  <div className="flex items-baseline gap-2">
                    <span className="font-medium text-muted-foreground tabular-nums">
                      {hit.index}.
                    </span>
                    {hit.url ? (
                      <a
                        href={hit.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-baseline gap-1 font-medium text-foreground hover:text-primary hover:underline"
                      >
                        <span>{hit.title ?? hit.url}</span>
                        <ExternalLinkIcon className="size-3 shrink-0 self-center" />
                      </a>
                    ) : (
                      <span className="font-medium text-foreground">
                        {hit.title ?? "(untitled)"}
                      </span>
                    )}
                  </div>
                  <div className="ms-5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-muted-foreground">
                    {hit.source && <span>{hit.source}</span>}
                    {hit.published && (
                      <span className="tabular-nums">{hit.published}</span>
                    )}
                    {hit.score !== null && (
                      <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] tabular-nums">
                        score {hit.score.toFixed(2)}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <pre className="aui-search-kb-inline-raw whitespace-pre-wrap break-words text-muted-foreground">
              {String(result ?? "").trim()}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

SearchKnowledgeBaseInline.displayName = "SearchKnowledgeBaseInline";

export { SearchKnowledgeBaseInline };

// Tool names we delegate to the inline renderer. Match is case-
// insensitive and tolerant of the humanized form ("Search knowledge
// base") we set in messageMapper.stepToPart() via humanizeToolName().
export function isSearchTool(toolName: string): boolean {
  const n = toolName.toLowerCase();
  return (
    n.includes("search_knowledge_base") ||
    n.includes("search knowledge base") ||
    n.includes("search_agrana_emails") ||
    n.includes("search agrana emails")
  );
}
